<main class="tfr-page-shell py-5 lg:py-10">

  <!-- Header -->
  <div class="flex items-center justify-between mb-6">
    <h1 class="text-[22px] font-bold tracking-tight text-midnight-indigo">Mis Reservas</h1>
    <button type="button" (click)="goExplore()"
     
      class="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border text-[12px] font-semibold transition-all duration-150 hover:bg-cloud-mist active:scale-[0.97] focus:outline-none border-midnight-indigo text-midnight-indigo bg-snow-white">
      <lucide-icon name="compass" class="h-3.5 w-3.5 shrink-0" />
      Explorar espacios
    </button>
  </div>

  @if (depositSyncMessage(); as msg) {
    <p class="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] text-emerald-900">
      {{ msg }}
    </p>
  }

  <!-- Loading -->
  @if (loading()) {
    <div class="flex flex-col items-center justify-center py-20 gap-3">
      <lucide-icon name="loader-circle" class="h-8 w-8 animate-spin text-steel-gray" />
      <p class="text-[13px] text-steel-gray">Cargando reservas…</p>
    </div>
  }

  <!-- Empty state -->
  @if (!loading() && bookings().length === 0) {
    <div class="flex flex-col items-center justify-center py-16 lg:py-20 text-center">
      <div
           class="h-14 w-14 rounded-2xl flex items-center justify-center mb-5 bg-cloud-mist border border-pale-gray">
        <lucide-icon name="ticket" class="h-7 w-7 text-steel-gray" />
      </div>
      <p class="text-[15px] font-semibold mb-1 text-midnight-indigo">
        A&uacute;n no tienes planes. &iquest;Empezamos?
      </p>
      <p class="text-[13px] max-w-xs mb-6 text-steel-gray">
        Explora los espacios disponibles y haz tu primera reserva.
      </p>
      <button type="button" (click)="goExplore()"
       
        class="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border text-[13px] font-semibold transition-all duration-150 hover:bg-cloud-mist active:scale-[0.97] focus:outline-none border-midnight-indigo text-midnight-indigo bg-snow-white">
        <lucide-icon name="compass" class="h-4 w-4 shrink-0" />
        Descubrir nuevos espacios
      </button>
    </div>
  }

  <!-- Tabs (solo cuando hay reservas) -->
  @if (!loading() && bookings().length > 0) {
    <nav class="flex gap-0.5 border-b border-pale-gray mb-5 -mx-0.5 px-0.5
                overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
         aria-label="Secciones de reservas">
      <button type="button" (click)="setBookingsTab('upcoming')"
              class="relative shrink-0 px-4 py-2.5 text-[13px] font-semibold transition-colors whitespace-nowrap"
              [class.text-midnight-indigo]="bookingsTab() === 'upcoming'"
              [class.text-steel-gray]="bookingsTab() !== 'upcoming'"
              [class.hover:text-slate-blue]="bookingsTab() !== 'upcoming'"
              [attr.aria-current]="bookingsTab() === 'upcoming' ? 'page' : null">
        <span class="inline-flex items-center gap-2">
          Próximas
          @if (activeBookings().length > 0) {
            <span class="inline-flex min-w-[1.25rem] h-5 items-center justify-center rounded-full px-1.5
                         text-[10px] font-bold tabular-nums bg-midnight-indigo text-snow-white">
              {{ activeBookings().length }}
            </span>
          }
        </span>
        @if (bookingsTab() === 'upcoming') {
          <span class="absolute left-0 right-0 -bottom-px h-0.5 bg-midnight-indigo rounded-full" aria-hidden="true"></span>
        }
      </button>
      <button type="button" (click)="setBookingsTab('history')"
              class="relative shrink-0 px-4 py-2.5 text-[13px] font-semibold transition-colors whitespace-nowrap"
              [class.text-midnight-indigo]="bookingsTab() === 'history'"
              [class.text-steel-gray]="bookingsTab() !== 'history'"
              [class.hover:text-slate-blue]="bookingsTab() !== 'history'"
              [attr.aria-current]="bookingsTab() === 'history' ? 'page' : null">
        Historial
        @if (bookingsTab() === 'history') {
          <span class="absolute left-0 right-0 -bottom-px h-0.5 bg-midnight-indigo rounded-full" aria-hidden="true"></span>
        }
      </button>
    </nav>
  }

  <!-- Tab: Próximas -->
  @if (!loading() && bookingsTab() === 'upcoming') {
    @if (activeBookings().length === 0 && bookings().length > 0) {
      <p class="py-10 text-center text-[13px] text-steel-gray">No tienes reservas próximas.</p>
    }
    <div class="flex flex-col gap-3">
      @for (booking of activeBookings(); track booking.id) {
        <div class="rounded-2xl border bg-white px-5 py-4 flex flex-col gap-3 border-pale-gray shadow-tfr-sm">

          <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div class="flex items-start gap-4 min-w-0">
              <div class="shrink-0 h-10 w-10 rounded-xl flex items-center justify-center mt-0.5 bg-action-blue/10">
                <lucide-icon name="building-2" class="h-5 w-5 text-glacier-blue" />
              </div>
              <div class="min-w-0">
                <p class="text-[14px] font-semibold truncate text-midnight-indigo">
                  {{ spaceNames()[booking.spaceId] || 'Cargando…' }}
                </p>
                <p class="text-[12px] mt-0.5 capitalize text-slate-blue">{{ formatDate(booking.date) }}</p>
                <p class="text-[11px] mt-0.5 tabular-nums text-steel-gray">{{ booking.startTime }} – {{ booking.endTime }}</p>
                <p class="mt-2 text-[12px] leading-relaxed text-slate-blue">{{ workflowText(booking) }}</p>
              </div>
            </div>
            <div class="flex flex-row sm:flex-col items-center sm:items-end gap-3 sm:gap-1.5 shrink-0">
              <span class="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold"
                    [class]="statusMeta[booking.status].bgClass + ' ' + statusMeta[booking.status].textClass">
                {{ statusMeta[booking.status].label }}
              </span>
              @if (booking.totalPrice) {
                <span class="text-[12px] font-semibold tabular-nums text-midnight-indigo">
                  {{ booking.totalPrice | number:'1.2-2' }}&nbsp;€
                </span>
              }
            </div>
          </div>

          @if (guestNeedsSecurityDepositAction(booking) || (booking.status === 'CONFIRMED' && booking.securityDepositStatus === 'submitted')) {
            <app-guest-security-deposit-panel [booking]="booking" />
          }

          <div class="pt-2 border-t flex flex-wrap justify-end gap-2 border-pale-gray">
            @if (canCancelFormalization(booking)) {
              <button type="button" (click)="openCancelModal(booking, $event)"
                      class="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border text-[12px] font-semibold transition-all duration-150 active:scale-[0.97] focus:outline-none border-red-200 text-red-800 bg-red-50 hover:bg-red-100">
                <lucide-icon name="circle-x" class="h-3.5 w-3.5 shrink-0" />
                Cancelar reserva
              </button>
            }
            <app-my-bookings-withdrawal
              [booking]="booking"
              [spaceName]="spaceNames()[booking.spaceId] || 'Tu reserva'"
              [spaceCancellationDays]="spaceCancellationDays()[booking.spaceId]"
              [formatDate]="formatDate" />
            <button type="button" (click)="goPrimaryAction(booking)"
                    class="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12px] font-semibold text-white transition-all duration-150 active:scale-[0.97] focus:outline-none bg-action-blue hover:brightness-110">
              <lucide-icon name="arrow-right" class="h-3.5 w-3.5 shrink-0" />
              {{ primaryActionLabel(booking) }}
            </button>
          </div>

        </div>
      }
    </div>
  }

  <!-- Tab: Historial -->
  @if (!loading() && bookingsTab() === 'history') {
    @if (pastBookings().length === 0) {
      <p class="py-10 text-center text-[13px] text-steel-gray">Aún no tienes reservas completadas o canceladas.</p>
    }
    <div class="flex flex-col gap-3">
      @for (booking of pastBookings(); track booking.id) {
        <div class="rounded-2xl border bg-white px-5 py-4 flex flex-col gap-3 border-pale-gray shadow-tfr-sm">

          <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div class="flex items-start gap-4 min-w-0">
              <div class="shrink-0 h-10 w-10 rounded-xl flex items-center justify-center mt-0.5 bg-pale-gray">
                <lucide-icon name="building-2" class="h-5 w-5 text-steel-gray" />
              </div>
              <div class="min-w-0">
                <p class="text-[14px] font-semibold truncate text-midnight-indigo">
                  {{ spaceNames()[booking.spaceId] || 'Cargando…' }}
                </p>
                <p class="text-[12px] mt-0.5 capitalize text-slate-blue">{{ formatDate(booking.date) }}</p>
                <p class="text-[11px] mt-0.5 tabular-nums text-steel-gray">{{ booking.startTime }} – {{ booking.endTime }}</p>
                <p class="mt-2 text-[12px] leading-relaxed text-slate-blue">{{ workflowText(booking) }}</p>
              </div>
            </div>
            <div class="flex flex-row sm:flex-col items-center sm:items-end gap-3 sm:gap-1.5 shrink-0">
              <span class="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold"
                    [class]="statusMeta[booking.status].bgClass + ' ' + statusMeta[booking.status].textClass">
                {{ statusMeta[booking.status].label }}
              </span>
              @if (booking.totalPrice) {
                <span class="text-[12px] font-semibold tabular-nums text-midnight-indigo">
                  {{ booking.totalPrice | number:'1.2-2' }}&nbsp;€
                </span>
              }
            </div>
          </div>

          <div class="pt-2 border-t flex flex-wrap justify-end gap-2 border-pale-gray">
            @if (canReviewBooking(booking)) {
              <button type="button" (click)="openReviewModal(booking, $event)"
                      class="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border text-[12px] font-semibold transition-all duration-150 active:scale-[0.97] focus:outline-none border-amber-200 text-amber-900 bg-amber-50 hover:bg-amber-100">
                <lucide-icon name="star" class="h-3.5 w-3.5 shrink-0" />
                Valorar
              </button>
            }
            <button type="button" (click)="goPrimaryAction(booking)"
                    class="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12px] font-semibold text-white transition-all duration-150 active:scale-[0.97] focus:outline-none bg-action-blue hover:brightness-110">
              <lucide-icon name="arrow-right" class="h-3.5 w-3.5 shrink-0" />
              {{ primaryActionLabel(booking) }}
            </button>
          </div>

        </div>
      }
    </div>
  }

</main>

@if (cancelTarget(); as target) {
  <div
       (click)="closeCancelModal()"
       class="fixed inset-0 z-[120] flex items-end sm:items-center justify-center px-4 pb-6 sm:pb-0 bg-slate-900/50 backdrop-blur-[1px]">
    <div
         (click)="$event.stopPropagation()"
         class="w-full max-w-md rounded-2xl border bg-white p-6 border-pale-gray shadow-tfr-sm">
      <div class="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 class="text-[17px] font-bold text-midnight-indigo">Cancelar reserva</h2>
          <p class="mt-1 text-[12px] text-steel-gray">
            {{ spaceNames()[target.spaceId] || 'Tu reserva' }} · {{ formatDate(target.date) }}
          </p>
        </div>
        <button type="button"
                (click)="closeCancelModal()"
                [disabled]="cancelSaving()"
                aria-label="Cerrar"
                class="h-8 w-8 flex items-center justify-center rounded-xl border border-pale-gray text-slate-blue shrink-0 disabled:opacity-50">
          <lucide-icon name="x" [size]="16" />
        </button>
      </div>

      <div class="space-y-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] leading-relaxed text-red-900">
        <p>
          <strong>No hay vuelta atrás:</strong> el hueco quedará disponible de inmediato para otros usuarios.
          Esta acción no se puede deshacer.
        </p>
        <p>
          <strong>Tasa de gestión online ({{ platformFeeAmount(target) | number:'1.2-2' }}&nbsp;€):</strong>
          se pierde aunque canceles la reserva. No es reembolsable según las condiciones aceptadas en el pago.
        </p>
      </div>

      <label class="mt-5 flex items-start gap-3 cursor-pointer select-none">
        <input type="checkbox"
               class="mt-0.5 h-4 w-4 rounded border-pale-gray text-midnight-indigo focus:ring-midnight-indigo/30"
               [checked]="cancelAcknowledged()"
               (change)="cancelAcknowledged.set($any($event.target).checked)" />
        <span class="text-[12px] leading-snug text-slate-blue">
          Entiendo que el hueco se liberará, que no podré recuperar la tasa de gestión online
          y que deseo cancelar esta reserva.
        </span>
      </label>

      @if (cancelError(); as err) {
        <p class="mt-3 text-[12px] text-red-600">{{ err }}</p>
      }

      <div class="mt-6 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
        <button type="button"
                (click)="closeCancelModal()"
                [disabled]="cancelSaving()"
                class="rounded-xl border px-4 py-2.5 text-[13px] font-semibold border-pale-gray text-slate-blue disabled:opacity-50">
          Volver
        </button>
        <button type="button"
                (click)="confirmCancelBooking()"
                [disabled]="!cancelAcknowledged() || cancelSaving()"
                class="rounded-xl px-4 py-2.5 text-[13px] font-semibold text-white bg-red-700 hover:bg-red-800 disabled:opacity-40 disabled:cursor-not-allowed">
          @if (cancelSaving()) {
            Cancelando&hellip;
          } @else {
            Confirmar cancelación
          }
        </button>
      </div>
    </div>
  </div>
}

@if (reviewTarget(); as target) {
  <div
       (click)="closeReviewModal()"
       class="fixed inset-0 z-[120] flex items-end sm:items-center justify-center px-4 pb-6 sm:pb-0 bg-slate-900/50 backdrop-blur-[1px]">
    <div
         (click)="$event.stopPropagation()"
         class="w-full max-w-md rounded-2xl border bg-white p-6 border-pale-gray shadow-tfr-sm">
      <div class="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 class="text-[17px] font-bold text-midnight-indigo">Valorar experiencia</h2>
          <p class="mt-1 text-[12px] text-steel-gray">
            {{ spaceNames()[target.spaceId] || 'Tu reserva' }} · {{ formatDate(target.date) }}
          </p>
        </div>
        <button type="button"
                (click)="closeReviewModal()"
                [disabled]="reviewSaving()"
                aria-label="Cerrar"
                class="h-8 w-8 flex items-center justify-center rounded-xl border border-pale-gray text-slate-blue shrink-0 disabled:opacity-50">
          <lucide-icon name="x" [size]="16" />
        </button>
      </div>

      <p class="text-[12px] text-steel-gray mb-2">Tu valoración (1–5 estrellas)</p>
      <div class="flex items-center gap-1 mb-4" role="group" aria-label="Seleccionar estrellas">
        @for (star of [1, 2, 3, 4, 5]; track star) {
          <button type="button"
                  (click)="setReviewRating(star)"
                  class="p-1 rounded-lg transition hover:bg-amber-50"
                  [attr.aria-label]="star + ' estrellas'"
                  [attr.aria-pressed]="reviewRating() >= star">
            <lucide-icon
              name="star"
              [size]="22"
              [class.text-amber-500]="reviewRating() >= star"
              [class.fill-amber-500]="reviewRating() >= star"
              [class.text-pale-gray]="reviewRating() < star" />
          </button>
        }
      </div>

      <label class="block text-[12px] font-semibold text-slate-blue mb-1" for="review-text">
        Comentario
      </label>
      <textarea
        id="review-text"
        rows="4"
        class="w-full rounded-xl border border-pale-gray px-3 py-2 text-[13px] text-midnight-indigo"
        [value]="reviewText()"
        (input)="onReviewTextInput($event)"
        [attr.maxlength]="reviewMaxLength"
        placeholder="Cuéntanos qué tal fue el espacio y la experiencia"></textarea>
      <p class="mt-1 text-[11px] text-steel-gray tabular-nums">
        {{ reviewTextLength() }} / {{ reviewMaxLength }} (mín. {{ reviewMinLength }})
      </p>

      @if (reviewError(); as err) {
        <p class="mt-3 text-[12px] text-red-600">{{ err }}</p>
      }

      <div class="mt-6 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
        <button type="button"
                (click)="closeReviewModal()"
                [disabled]="reviewSaving()"
                class="rounded-xl border px-4 py-2.5 text-[13px] font-semibold border-pale-gray text-slate-blue disabled:opacity-50">
          Cancelar
        </button>
        <button type="button"
                (click)="submitReview()"
                [disabled]="!canSubmitReview()"
                class="rounded-xl px-4 py-2.5 text-[13px] font-semibold text-white bg-action-blue hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed">
          @if (reviewSaving()) {
            Enviando&hellip;
          } @else {
            Publicar valoración
          }
        </button>
      </div>
    </div>
  </div>
}
